import axios from 'axios'
import { getValidAccesToken, setTokenExpiry } from './utils'

const API_BASE_URL = import.meta.env.VITE_API_BASE_DEV_URL

const tokenVaultApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }
})

tokenVaultApi.interceptors.request.use(
  request => {
    return request
  },
  error => {
    console.error('Request error:', error)
    return Promise.reject(error)
  }
)

tokenVaultApi.interceptors.response.use(
  response => {
    console.log('Response received:', response.status)
    return response
  },
  error => {
    console.error('Response error:', error.response)
    return Promise.reject(error)
  }
)

export async function refreshAccessToken () {
  const refreshToken = sessionStorage.getItem('refreshToken')
  if (!refreshToken) throw new Error('No refresh token')

  const tokenBody = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: import.meta.env.VITE_COGNITO_CLIENT_ID,
    refresh_token: refreshToken
  }).toString()

  const response = await axios.post(
    `${import.meta.env.VITE_API_AUTH_BASE}/oauth2/token?`,
    tokenBody,
    { headers: { 'content-type': 'application/x-www-form-urlencoded' } }
  )

  sessionStorage.setItem('accessToken', response.data.access_token)
  if (response.data.refresh_token) {
    sessionStorage.setItem('refreshToken', response.data.refresh_token)
  }
  if (response.data.expires_in) {
    setTokenExpiry(response.data.expires_in)
  }
  return response.data.access_token
}

export async function loginOAuth2 (baseUrl, tokenBody) {
  try {
    const newUrl = `${baseUrl}/oauth2/token?`
    const response = await axios.post(newUrl, tokenBody, {
      headers: {
        accept: 'application/json',
        'content-type': 'application/x-www-form-urlencoded'
      }
    })
    return response.data
  } catch (error) {
    return { data: null, error: error.response?.data || error.message }
  }
}

// list metadata endpoint: GET /credentials
export async function getAllCredentials () {
  const accessToken = getValidAccesToken() || (await refreshAccessToken())
  try {
    const response = await tokenVaultApi.get('/credentials?limit=25', {
      headers: {
        authorization: `Bearer ${accessToken}`
      }
    })
    // const response = await tokenVaultApi.get('/credentials?limit=25')
    console.log('Data received:', response.data)
    return { data: response.data, error: null }
  } catch (error) {
    return { data: null, error: error.response?.data || error.message }
  }
}

// credential with secrets endpoint: GET /credentials/{api_name}
export async function getApiCredential (api_name) {
  try {
    const accessToken = sessionStorage.getItem('accessToken')
    const response = await tokenVaultApi.get(`/credentials/${api_name}`, {
      headers: {
        authorization: `Bearer ${accessToken}`
      }
    })
    console.log('Received credential:', response.data)
    return { data: response.data, error: null }
  } catch (error) {
    return { data: null, error: error.response?.data || error.message }
  }
}

// create new credential endpoint: POST /credentials
export async function createApiCredential (data) {
  try {
    const accessToken = sessionStorage.getItem('accessToken')
    const response = await tokenVaultApi.post('/credentials', data, {
      headers: {
        authorization: `Bearer ${accessToken}`
      }
    })
    return { data: response.data, error: null }
  } catch (error) {
    return { data: null, error: error.response?.data || error.message }
  }
}

// replace credential endpoint: PUT /credentials/{api_name}
export async function replaceApiCredential (data) {
  try {
    const response = await tokenVaultApi.put('/credentials', data)
    return { data: response.data, error: null }
  } catch (error) {
    return { data: null, error: error.response?.data || error.message }
  }
}

// merge credential endpoint: PATCH /credentials/{api_name}
export async function mergeApiCredential () {
  try {
    const response = await tokenVaultApi.patch('/credentials')
    return { data: response.data, error: null }
  } catch (error) {
    return { data: null, error: error.response?.data || error.message }
  }
}

// remove credential endpoint: DELETE /credentials/{api_name}
export async function deleteApiCredential () {
  try {
    const response = await tokenVaultApi.delete('/credentials')
    return { data: response.data, error: null }
  } catch (error) {
    return { data: null, error: error.response?.data || error.message }
  }
}
